import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Button } from '../Button';
import { Paper } from '../Paper';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.overlay.scrim};
`;

const ModalContent = styled.div`
  max-width: 400px;
  width: 90%;
`;

const MessageText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'filled' | 'outlined' | 'text';
  confirmColor?: 'primary' | 'error';
  loading?: boolean;
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Подтвердить',
  cancelText = 'Отмена',
  confirmVariant = 'filled',
  confirmColor = 'primary',
  loading = false,
}: ConfirmModalProps) {
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleConfirm = () => {
    onConfirm();
  };

  if (!open) {
    return null;
  }

  return createPortal(
    <Overlay onClick={onClose} role="presentation">
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Paper padding={24} shadow={2}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>{title}</h2>
          {message && <MessageText>{message}</MessageText>}
          <Actions>
            <Button variant="outlined" color="primary" onClick={onClose} disabled={loading}>
              {cancelText}
            </Button>
            <Button
              variant={confirmVariant}
              color={confirmColor}
              onClick={handleConfirm}
              loading={loading}
            >
              {confirmText}
            </Button>
          </Actions>
        </Paper>
      </ModalContent>
    </Overlay>,
    document.body,
  );
}
